import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { SmsService } from '@libs/shared/api';
import { BasicSnackbarService } from '@libs/shared/ui/basic-snackbar/basic-snackbar.service';
import { injectCoreFeature } from '@sprint-expert/app/core/state/core.service';
import { environment } from '@sprint-expert/environments/environment';
import { catchError, of, take } from 'rxjs';
import { tap } from 'rxjs/operators';

type ExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup = FormGroup<{
  phoneNumber: FormControl<string>;
}>;

@Injectable()
export class ExpertWorkspaceReferralInvitationDeepLinkDetailService {
  private readonly fb = inject(FormBuilder);
  private readonly core = injectCoreFeature();
  readonly basicSnackBar = inject(BasicSnackbarService);
  private readonly smsService = inject(SmsService);
  private readonly basicSnackbarService = inject(BasicSnackbarService);

  formGroup: ExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup;
  currentUser$$ = this.core.currentUser$$;
  currentExpertWorkspace$$ = this.core.currentExpertWorkspace$$;

  expertWebUrl = environment.webUrl.expert;
  invitationText$$ = signal<string>(undefined);

  enter() {
    this.setTexts(this.currentExpertWorkspace$$().code);
    this.formGroup =
      this.initializeExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup();
  }

  private initializeExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup(): ExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup {
    return this.fb.group({
      phoneNumber: [null, [Validators.required, phoneNumberPatternValidator]],
    }) as ExpertWorkspaceReferralInvitationDeepLinkDetailFormGroup;
  }

  sendExpertWorkspaceReferralInvitationDeepLinkDetailSms() {
    this.smsService
      .sendLms(
        this.formGroup.value.phoneNumber,
        this.currentExpertWorkspace$$().name,
        this.invitationText$$()
      )
      .pipe(
        take(1),
        tap(() => {
          this.formGroup.reset();
          this.basicSnackbarService.open('초대 메시지를 전송했습니다.');
        }),
        catchError((e: HttpErrorResponse) => {
          this.basicSnackbarService.open('초대 메시지 전송에 실패했습니다.');
          return of(null);
        })
      )
      .subscribe();
  }

  private setTexts(code: string) {
    this.invitationText$$.set(this.buildInvitationMessage(code));
  }

  copyInvitationTextToClipboard() {
    navigator.clipboard.writeText(this.invitationText$$()).then(
      () => {
        // 복사 성공 시 메시지
        this.basicSnackBar.open('초대 메시지가 클립보드에 복사되었습니다.');
      },
      err => {
        // 복사 실패 시 메시지
        this.basicSnackBar.open('복사 실패: ' + err);
      }
    );
  }

  copyExpertWorkspaceCode() {
    navigator.clipboard.writeText(this.core.currentUser$$().username).then(
      () => {
        // 복사 성공 시 메시지
        this.basicSnackBar.open('코치 아이디가 클립보드에 복사되었습니다.');
      },
      err => {
        // 복사
        this.basicSnackBar.open('복사 실패: ' + err);
      }
    );
  }

  private buildInvitationMessage(code: string): string {
    const inviterNickname = this.currentUser$$()?.nickname ?? '코치';
    const workspaceName = this.currentExpertWorkspace$$()?.name ?? '스프린트';

    return `[${workspaceName} 초대]
${inviterNickname} 코치님이 스프린트로 초대합니다!

*1개월 무료 혜택*
초대받은 친구와 코치님 모두에게 스프린트 1개월 무료 이용권(크레딧) 지급!

*참여 방법*
1. 링크 접속 후 회원가입: ${this.expertWebUrl}
2. 센터 생성
3. 요금제 선택 후 '다음으로' 클릭
4. 추천인 코드 입력: @${code}
5. 첫 결제 완료 시 자동으로 크레딧 지급

지금 바로 참여하고 혜택 받으세요!`;
  }
}

function phoneNumberPatternValidator(
  control: AbstractControl
): ValidationErrors | null {
  const phoneNumbers: string[] = control.value;
  if (!phoneNumbers || !Array.isArray(phoneNumbers)) {
    return null;
  }

  // Update the regex pattern to match '010-xxxx-xxxx'
  const isValid = phoneNumbers.every(phoneNumber =>
    /^010-\d{4}-\d{4}$/.test(phoneNumber)
  );
  return isValid ? null : { invalidPhoneNumber: true };
}
